import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalBody,
  CardBody,
  Nav, 
  NavItem, 
  NavLink, 
  TabContent,
  TabPane,
  Button,
  Row,
  Col,
  FormGroup,
  Label,
  Input
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { PaginationControl, updateAtPath } from '../../../pages/AI/Cases/CaseDetails2';

export function RecursiveEditor({
  value = "",
  path = [],
  onChange,
  name,
  keys,
  readOnly = false,
}) {
  const { t } = useTranslation();
  // use pagination only for array of objects
  const [page, setPage] = useState(0);
  const isObject = value && typeof value === "object" && !Array.isArray(value);
  const isArray = Array.isArray(value);

  const handlePrimitiveChange = (newVal) => {
    onChange(path, newVal);
  };

    if (isObject) {
      const objectKeys = keys
        ? Object.keys(value).filter((k) => !keys.includes(k))
        : Object.keys(value);

      return (
        <div style={{ borderLeft: "2px solid #eee", paddingLeft: 12 }}>
          {name && <div style={{ fontWeight: 600 }}>{t(name)}</div>}

          <Row>
            {objectKeys.map((key) => (
              <RecursiveEditor
                key={key}
                keys={keys}
                name={key}
                value={value[key]}
                path={[...path, key]}
                onChange={onChange}
                readOnly={readOnly}
              />
            ))}
          </Row>
        </div>
      );
    }

  else if (isArray) {
    const isArrayOfObjects =
      value.length > 0 && value.every((item) => typeof item === "object" && item !== null);



    return (
      <div style={{ borderLeft: "2px dashed #eee", paddingLeft: 12, marginBottom: 8 }}>
        <div className="d-flex justify-content-between align-items-center">
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {t(name)} ({t("length")}: {value.length})
          </div>

          {(!readOnly && isArrayOfObjects) && (
            <div style={{ marginTop: 6 }}>
              <Button
                size="sm"
                color="primary"
                outline
                onClick={() => {
                  let newItem;

                  if (typeof value[0] === "object" && value[0] !== null && !Array.isArray(value[0])) {
                    // clone object structure with empty string values
                    newItem = Object.fromEntries(Object.keys(value[0]).map((key) => [key, ""]));
                  } else {
                    // fallback for primitive array (string, number, etc.)
                    newItem = "";
                  }

                  const newArr = [...value, newItem];
                  onChange(path, newArr);
                }}
              >
                {t("Add")}
              </Button>
            </div>
          )}
        </div>


        {isArrayOfObjects ? (
          <>
            <RecursiveEditor
              name={`[${page}]`}
              value={value[page]}
              path={[...path, page]}
              onChange={onChange}
              keys={keys}
              readOnly={readOnly}
            />

            {!readOnly && (
              <div style={{ marginTop: 8 }}>
                <Button
                  size="sm"
                  color="danger"
                  outline
                  onClick={() => {
                    const newArr = [...value];
                    newArr.splice(page, 1);
                    onChange(path, newArr);
                    if (page >= newArr.length) setPage(Math.max(0, newArr.length - 1));
                  }}
                >
                  {t("Remove")}
                </Button>
              </div>
            )}

            {value.length > 1 && (
              <div style={{ marginTop: 10 }}>
                <PaginationControl
                  total={value.length}
                  current={page}
                  onChange={(p) => {
                    setPage(p);
                  }}
                />
              </div>
            )}
          </>
        ) : (
          <>
            {value.map((item, idx) => (
              <div key={idx} style={{ marginBottom: 8 }}>
                <RecursiveEditor
                  name={`[${idx}]`}
                  value={item}
                  path={[...path, idx]}
                  onChange={onChange}
                  keys={keys}
                  readOnly={readOnly}
                />
                {!readOnly && (
                  <div>
                    <Button
                      size="sm"
                      color="danger"
                      outline
                      onClick={() => {
                        const newArr = [...value];
                        newArr.splice(idx, 1);
                        onChange(path, newArr);
                      }}
                    >
                      {t("Remove")}
                    </Button>
                  </div>
                )}

                {!readOnly && (
                  <div style={{ marginTop: 6 }}>
                    <Button
                      size="sm"
                      color="primary"
                      outline
                      onClick={() => {
                        const newArr = [...value, typeof value[0] === "object" ? {} : ""];
                        onChange(path, newArr);
                      }}
                    >
                      {t("Add")}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    );
  }

  else {
    const asString = value === null ? "" : String(value);
    const useTextarea = asString.length > 60 || asString.includes("\n");

    return (
        <Col md={6}>
          <FormGroup style={{ marginBottom: 8 }}>
            <Label style={{ fontWeight: 600, fontSize: 14 }}>{t(name)}</Label>
            {useTextarea ? (
            <Input
                type="textarea"
                value={asString}
                rows={5}
                onChange={(e) => handlePrimitiveChange(e.target.value)}
                readOnly={readOnly}
            />
            ) : (
            <Input
                type="text"
                value={asString}
                onChange={(e) => handlePrimitiveChange(e.target.value)}
                readOnly={readOnly}
            />
            )}
          </FormGroup>
        </Col>
    );
  }
}

const SectionModal = ({ show, setShow, selectedRow = {}, setSelectedRow, onSaveClickHandler }) => {
    const { t } = useTranslation();
    const [articleIndex, setArticleIndex] = useState(0);
    const [activeTab, setActiveTab] = useState("section");


    const handleTabChange = (tab) => {
        if (tab === activeTab) return;
        setActiveTab(tab);

        if (tab === "article") {
            setArticleIndex(0);
        }
    }

    const onChangeValue = (path, newVal) => {
      const updated = updateAtPath(selectedRow, path, newVal);
      setSelectedRow(updated);
    };

    useEffect(() => {
        if (show) {
            setActiveTab('section');
            setArticleIndex(0);
        }

    }, [show])

  return (
    <Modal isOpen={show} centered={true} size="md">
        <ModalBody className="py-3 px-5">
            <CardBody>
                <div className="listjs-table" id="customerList">
                    <Nav tabs style={{ marginBottom: 16 }}>
                        <NavItem>
                            <NavLink
                            className={activeTab === "section" ? "active" : ""}
                            onClick={() => handleTabChange("section")}
                            style={{ cursor: "pointer" }}
                            >
                            {t('Section')}
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                            className={activeTab === "article" ? "active" : ""}
                            onClick={() => handleTabChange("article")}
                            style={{ cursor: "pointer" }}
                            >
                            {t('Article')}
                            </NavLink>
                        </NavItem>
                    </Nav>

                    <TabContent activeTab={activeTab}>
                        <TabPane tabId="section">
                            <div className="my-3 fs-15">
                                <h4>{t('Sections')}</h4>
                                { Object.keys(selectedRow?.Sections || {}).map((key, index) => {
                                    return (
                                        <div className="mt-3" key={index}>
                                            <RecursiveEditor
                                                name={key}
                                                value={selectedRow?.Sections[key]}
                                                path={["Sections", key]}
                                                onChange={onChangeValue} 
                                            />
                                        </div>
                                    )
                                })}
                            </div>
                        </TabPane>
                    </TabContent>
                
                    <TabContent activeTab={activeTab}>
                        <TabPane tabId="article">
                            <div className="my-3 fs-15">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h4>{t('Articles')}</h4>

                                    <div>
                                        <Button
                                        size="sm"
                                        color="secondary"
                                        outline
                                        onClick={() => {
                                            // add new blank paragraph
                                            const newArticleRange = {
                                                ...(selectedRow.ArticleRange),
                                                articleRanges: [
                                                    ...(selectedRow?.ArticleRange?.articleRanges || []),
                                                    {
                                                        type: "",
                                                        end_page: "",
                                                        start_page: "",
                                                        article_number: "",
                                                        heading_text_exact: ""
                                                    }
                                                ]
                                            };
                                            onChangeValue(["ArticleRange"], newArticleRange);
                                            setArticleIndex((selectedRow?.ArticleRange?.articleRanges || []).length)
                                        }}>
                                        {t('Add')}
                                        </Button>
                                    </div>
                                </div>

                                { selectedRow.ArticleRange ? <>
                                    <div className="mt-3">
                                        <RecursiveEditor
                                            value={selectedRow.ArticleRange?.articleRanges[articleIndex]}
                                            path={["ArticleRange", "articleRanges", articleIndex]}
                                            onChange={onChangeValue}
                                        />
                                    </div>
        
                                    <PaginationControl
                                        total={(selectedRow.ArticleRange?.articleRanges || []).length || 0}
                                        current={articleIndex}
                                        onChange={p => setArticleIndex(p)}
                                    />
                                </> : null

                                }

                                <div>
                                    <Button
                                        size="sm"
                                        color="danger"
                                        outline
                                        onClick={() => {
                                        // remove current paragraph
                                            const obj = {...selectedRow.ArticleRange};
                                            obj.articleRanges?.splice(articleIndex, 1);

                                            onChangeValue(["ArticleRange"], obj);
                                            setArticleIndex(Math.max(0, articleIndex - 1));
                                        }}
                                    >
                                        {t('Delete')}
                                    </Button>
                                </div>

                            </div>
                        </TabPane>
                    </TabContent>
                </div>
            </CardBody>


            <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
                <button
                    type="button"
                    className="btn w-sm btn-light"
                    data-bs-dismiss="modal"
                    onClick={() => setShow(false)}
                >
                    {t("Close")}
                </button>
                <button
                    type="button"
                    className="btn w-sm btn-danger d-flex align-items-center"
                    onClick={onSaveClickHandler}
                >
                    {t('Yes, Save it')}
                </button>
            </div>
        </ModalBody>
    </Modal>
  )
}

export default React.memo(SectionModal);